import { z } from 'zod';

/**
 * Lifecycle states for a Conversation. Transitions are emitted by the
 * conversation service (Phase 6 owns `nextState()`); this module only
 * defines the closed set of valid states.
 */
export const ConversationStateSchema = z.enum([
  'open',
  'awaiting_agent',
  'awaiting_contact',
  'resolved',
]);

export type ConversationState = z.infer<typeof ConversationStateSchema>;

/**
 * Lifecycle events that drive `nextState()` (Phase 6 D-08). Every event
 * the conversation service emits maps to at most one outgoing state per
 * `current` state; tuples not present in the transition table return
 * `null` from `nextState()` (no-op, no throw).
 */
export const LifecycleEventSchema = z.enum([
  'inbound_persisted',
  'agent_replied',
  'inactivity_timeout',
  'resolve',
]);

export type LifecycleEvent = z.infer<typeof LifecycleEventSchema>;

/**
 * Pure transition function. Returns the next `ConversationState` for a
 * `(current, event)` tuple, or `null` when the tuple is not mapped
 * (e.g. `agent_replied` from `'open'` is a no-op — caller must treat
 * `null` as "ignore this event"; never throws).
 *
 * The `switch (event)` is exhaustive-checked by TypeScript — adding a new
 * variant to `LifecycleEventSchema` surfaces here as a compile error
 * until a new case is added (D-08).
 */
export function nextState(
  current: ConversationState,
  event: LifecycleEvent,
): ConversationState | null {
  switch (event) {
    case 'inbound_persisted':
      return current === 'resolved' ? null : 'awaiting_agent';
    case 'agent_replied':
      return current === 'awaiting_agent' ? 'awaiting_contact' : null;
    case 'inactivity_timeout':
      return current === 'awaiting_contact' ? 'resolved' : null;
    case 'resolve':
      return current === 'resolved' ? null : 'resolved';
    default: {
      const exhaustive: never = event;
      return exhaustive;
    }
  }
}
